import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useFetchRefactored from 'civic-champs-shared/api/hooks/useFetchRefactored'
import { useCurrentOrg } from 'auth/hooks'
import format from 'civic-champs-shared/utils/format'
import { MESSAGES_FETCH_ALL_PAGE_SIZE } from 'messages/hooks/messagesServerSideSelectionConstants'
import { AutomatedMessage } from '../types'
import { CustomMessagesDateRange } from './useFetchCustomMessages'

const DEFAULT_PAGE_SIZE = 25

const buildFilterParams = ({ search, dateRange }: { search: string; dateRange: CustomMessagesDateRange }) => ({
  ...(search ? { search } : {}),
  ...(dateRange?.startDate ? { startDate: format.dateForUrl(dateRange.startDate) } : {}),
  ...(dateRange?.endDate ? { endDate: format.dateForUrl(dateRange.endDate) } : {}),
})

export const useFetchAutomatedMessages = (dateRange: CustomMessagesDateRange) => {
  const organization = useCurrentOrg()
  const [messages, setMessages] = useState<AutomatedMessage[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [request] = useFetchRefactored()
  const searchRef = useRef('')
  const lastFilterKeyRef = useRef<string | null>(null)

  const filterKey = useMemo(() => JSON.stringify(buildFilterParams({ search, dateRange })), [search, dateRange])

  const refetch = useCallback(
    async ({ includeCount }: { includeCount?: boolean } = {}) => {
      setLoading(true)
      const filterParams = buildFilterParams({ search, dateRange })
      const shouldFetchCount = includeCount ?? lastFilterKeyRef.current !== filterKey

      if (shouldFetchCount) {
        lastFilterKeyRef.current = filterKey
      }

      const pageRequest = request({
        method: 'get',
        url: `/organizations/${organization.id}/automated-messages-paged`,
        config: {
          queryStringParameters: {
            ...filterParams,
            limit: pageSize,
            offset: pageIndex * pageSize,
            sortBy: 'createdAt',
            sortDir: 'desc',
          },
        },
      }) as Promise<AutomatedMessage[]>

      try {
        if (shouldFetchCount) {
          const [summary, page] = await Promise.all([
            request({
              method: 'get',
              url: `/organizations/${organization.id}/automated-messages-paged-count`,
              config: { queryStringParameters: filterParams },
            }) as Promise<{ count: number }>,
            pageRequest,
          ])
          setTotalCount(summary.count)
          setMessages(page)
        } else {
          setMessages(await pageRequest)
        }
      } catch {
        if (shouldFetchCount) {
          setTotalCount(0)
        }
        setMessages([])
      } finally {
        setLoading(false)
      }
    },
    [dateRange, filterKey, organization.id, pageIndex, pageSize, request, search],
  )

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1)
    if (pageIndex > maxPage) {
      setPageIndex(maxPage)
    }
  }, [totalCount, pageSize, pageIndex])

  useEffect(() => {
    setPageIndex(0)
  }, [dateRange])

  const onSearchChange = useCallback((query: string) => {
    if (searchRef.current === query) {
      return
    }
    searchRef.current = query
    setSearch(query)
    setPageIndex(0)
  }, [])

  const handlePageSizeChange = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize)
    setPageIndex(0)
  }, [])

  const fetchAllMatchingInFlightRef = useRef<Promise<AutomatedMessage[]> | null>(null)

  const fetchAllMatchingMessages = useCallback(async (): Promise<AutomatedMessage[]> => {
    if (fetchAllMatchingInFlightRef.current) {
      return fetchAllMatchingInFlightRef.current
    }

    const fetchPromise = (async () => {
      const filterParams = buildFilterParams({ search, dateRange })
      const all: AutomatedMessage[] = []
      let offset = 0

      while (true) {
        const page = (await request({
          method: 'get',
          url: `/organizations/${organization.id}/automated-messages-paged`,
          config: {
            queryStringParameters: {
              ...filterParams,
              limit: MESSAGES_FETCH_ALL_PAGE_SIZE,
              offset,
              sortBy: 'createdAt',
              sortDir: 'desc',
            },
          },
        })) as AutomatedMessage[]

        all.push(...page)
        if (page.length < MESSAGES_FETCH_ALL_PAGE_SIZE) {
          break
        }
        offset += MESSAGES_FETCH_ALL_PAGE_SIZE
      }

      return all
    })()

    fetchAllMatchingInFlightRef.current = fetchPromise

    try {
      return await fetchPromise
    } finally {
      if (fetchAllMatchingInFlightRef.current === fetchPromise) {
        fetchAllMatchingInFlightRef.current = null
      }
    }
  }, [dateRange, organization.id, request, search])

  return {
    messages,
    totalCount,
    loading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize: handlePageSizeChange,
    onSearchChange,
    refetch,
    filterKey,
    fetchAllMatchingMessages,
  }
}
